import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setPartnerProfile, setView } from './onboardingSlice';
import { savePartnerDetails } from './partnerApi';
import { getOnboardingSubject } from '../../api/client';
import { fetchCountries } from '../../api/meta';

type Country = { id: string; name: string };

function looksLikeEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function looksLikePhone(value: string) {
  return value.replace(/\D/g, '').length >= 8;
}

const PartnerDetails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { formData, partnerProfile, partnerConditionsAcceptedLocal } = useSelector((s: RootState) => s.onboarding);
  const subject = getOnboardingSubject();
  const emailLocked = subject?.channel === 'email';
  const phoneLocked = subject?.channel === 'phone';

  const [companyName, setCompanyName] = useState(partnerProfile?.company_name || '');
  const [registrationNumber, setRegistrationNumber] = useState(partnerProfile?.company_registration_number || '');
  const [vatNumber, setVatNumber] = useState(partnerProfile?.vat_number || '');
  const [name, setName] = useState(partnerProfile?.name || '');
  const [email, setEmail] = useState(
    partnerProfile?.contact_email
    || (emailLocked ? subject?.value : '')
    || (looksLikeEmail(formData.email) ? formData.email : ''),
  );
  const [phone, setPhone] = useState(
    partnerProfile?.contact_phone
    || (phoneLocked ? subject?.value : '')
    || (looksLikePhone(formData.phone) ? formData.phone : ''),
  );
  const [address, setAddress] = useState(partnerProfile?.address || '');
  const [city, setCity] = useState(partnerProfile?.city || '');
  const [postalCode, setPostalCode] = useState(partnerProfile?.postal_code || '');
  const [country, setCountry] = useState<Country | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchCountries();
        if (cancelled) return;
        setCountries(list);
        const code = partnerProfile?.country_code;
        if (code) setCountry(list.find((c) => c.id === code) || null);
      } catch (_) { /* country stays optional */ }
    })();
    return () => { cancelled = true; };
  }, [partnerProfile?.country_code]);

  const submit = async () => {
    setError('');
    if (companyName.trim().length < 2) {
      setError('Enter the company name.');
      return;
    }
    if (!registrationNumber.trim()) {
      setError('Enter the company registration number.');
      return;
    }
    if (name.trim().length < 2) {
      setError('Enter the contact name.');
      return;
    }
    if (!looksLikeEmail(email)) {
      setError('Enter a valid contact email.');
      return;
    }
    setBusy(true);
    try {
      const row = await savePartnerDetails({
        name: name.trim(),
        company_name: companyName.trim(),
        company_registration_number: registrationNumber.trim(),
        vat_number: vatNumber.trim(),
        contact_email: email.trim(),
        contact_phone: phone.trim(),
        address: address.trim(),
        city: city.trim(),
        postal_code: postalCode.trim(),
        country_code: country?.id || '',
        conditions_accepted: partnerConditionsAcceptedLocal || partnerProfile?.partner_conditions_status === 'COMPLETED',
      });
      dispatch(setPartnerProfile(row));
      dispatch(setView('hub'));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save partner details');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button variant="text" onClick={() => dispatch(setView('hub'))} sx={{ mb: 1, px: 0 }}>← Back to overview</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TextField
        label="Company name"
        fullWidth
        margin="normal"
        required
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
      />
      <TextField
        label="Company registration number"
        fullWidth
        margin="normal"
        required
        value={registrationNumber}
        onChange={(e) => setRegistrationNumber(e.target.value)}
      />
      <TextField
        label="VAT number"
        fullWidth
        margin="normal"
        value={vatNumber}
        onChange={(e) => setVatNumber(e.target.value)}
      />
      <TextField
        label="Contact name"
        fullWidth
        margin="normal"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        label="Contact email"
        type="email"
        fullWidth
        margin="normal"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={emailLocked}
        helperText={emailLocked ? 'Taken from your sign-in' : 'We will use this email for the partner agreement'}
      />
      <TextField
        label="Contact phone"
        fullWidth
        margin="normal"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        disabled={phoneLocked}
        helperText={phoneLocked ? 'Taken from your sign-in' : undefined}
      />
      <TextField label="Address" fullWidth margin="normal" value={address} onChange={(e) => setAddress(e.target.value)} />
      <TextField label="City" fullWidth margin="normal" value={city} onChange={(e) => setCity(e.target.value)} />
      <TextField label="Postal code" fullWidth margin="normal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
      <Autocomplete
        options={countries}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={country}
        onChange={(_e, value) => setCountry(value)}
        renderInput={(params) => <TextField {...params} label="Country" margin="normal" />}
      />
      <Button variant="contained" onClick={submit} disabled={busy} sx={{ mt: 2 }}>
        {busy ? 'Saving…' : 'Save and continue'}
      </Button>
    </Box>
  );
};

export default PartnerDetails;
